import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import type { IntentKind } from "@spectree/shared";
import type { FogbugzBugBarResult } from "@ttc-spectree/toolbox-contract/fogbugz-formulator";
import type {
  FogbugzEvtxDigestResponse,
  FogbugzEvtxReviewedPostBody,
} from "@ttc-spectree/toolbox-contract/fogbugz";

// ===== Shared Query Key Factory =====

export const fogbugzKeys = {
  all: ["fogbugz"] as const,
  cases: () => [...fogbugzKeys.all, "cases"] as const,
  case: (id: number) => [...fogbugzKeys.all, "cases", id] as const,
  interview: (id: string) => [...fogbugzKeys.all, "interview", id] as const,
  interviewIntent: (id: string) => [...fogbugzKeys.all, "interview", id, "intent"] as const,
  routingOptions: () => [...fogbugzKeys.all, "interview", "routing-options"] as const,
};

// ===== Type Definitions =====

export interface FogBugzCase {
  ixBug: number;
  sTitle: string;
  sStatus: string;
  ixStatus: number;
  sPriority: string;
  ixPriority: number;
  sProject?: string;
  ixProject?: number;
  sArea?: string;
  ixArea?: number;
  sFixFor?: string;
  ixFixFor?: number;
  sPersonAssignedTo?: string;
  ixPersonAssignedTo?: number;
  sCategory?: string;
  dtLastUpdated?: string;
  events?: CaseEvent[];
}

export interface CaseEvent {
  ixBugEvent: number;
  evt: number;
  sVerb: string;
  sPerson: string;
  dt: string;
  sChanges?: string;
  sHtml?: string;
  sText?: string;
  rgAttachments?: Attachment[];
}

export interface Attachment {
  sFileName: string;
  sURL: string;
}

export interface LinkedSpecTreeItem {
  type: "epic-request" | "epic";
  id: string;
  identifier: string;
  title: string;
  status?: string;
}

export interface CaseListResponse {
  data: FogBugzCase[];
  meta: {
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface CaseDetailResponse {
  data: {
    case: FogBugzCase;
    linkedSpecTreeItem?: LinkedSpecTreeItem | null;
    fogbugzBaseUrl?: string | null;
  };
}

export interface CaseUpdateInput {
  title?: string;
  project?: string;
  area?: string;
  milestone?: string;
  priority?: number | string;
}

// ===== Ticket Management Hooks (ENG-1055) =====

/**
 * Hook to list FogBugz cases with page-based pagination.
 *
 * Pass `options.enabled = false` to skip the request (e.g., while the user
 * is loading or has no FogBugz API key configured) — this avoids an
 * inevitable 400/503 round-trip on every page mount.
 */
export function useFogbugzCases(
  params: { query?: string; page?: number; pageSize?: number },
  options?: { enabled?: boolean }
) {
  // Normalize params so queryKey and URL are always consistent
  const normalizedQuery = params.query?.trim() || undefined;
  const normalizedPage = params.page ?? 1;
  const normalizedPageSize = params.pageSize ?? 25;

  return useQuery<CaseListResponse>({
    queryKey: [...fogbugzKeys.cases(), { query: normalizedQuery, page: normalizedPage, pageSize: normalizedPageSize }],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (normalizedQuery) searchParams.set("query", normalizedQuery);
      if (normalizedPage > 1) searchParams.set("page", String(normalizedPage));
      if (normalizedPageSize !== 25) searchParams.set("pageSize", String(normalizedPageSize));

      const queryString = searchParams.toString();
      return api.get<CaseListResponse>(`/fogbugz/cases${queryString ? `?${queryString}` : ""}`);
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to get a single FogBugz case
 */
export function useFogbugzCase(id: number | null) {
  return useQuery<CaseDetailResponse>({
    queryKey: fogbugzKeys.case(id ?? 0),
    queryFn: () => api.get<CaseDetailResponse>(`/fogbugz/cases/${id}`),
    enabled: id !== null,
  });
}

/**
 * Hook to update a FogBugz case
 */
export function useUpdateCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...fields }: CaseUpdateInput & { id: number }) =>
      api.put<{ data: FogBugzCase; message: string }>(`/fogbugz/cases/${id}`, fields),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: fogbugzKeys.case(variables.id) });
      queryClient.invalidateQueries({ queryKey: fogbugzKeys.cases() });
    },
  });
}

/**
 * Hook to add a comment to a case
 */
export function useAddComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, text }: { id: number; text: string }) =>
      api.post<{ data: FogBugzCase; message: string }>(`/fogbugz/cases/${id}/comments`, { text }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: fogbugzKeys.case(variables.id) });
    },
  });
}

/**
 * Hook to attach a file to a case
 */
export function useAttachFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);

      // Route through ApiClient.request so we get auth headers, refresh-token
      // handling, and consistent error shapes. The client now skips
      // Content-Type for FormData bodies so the browser sets the correct
      // multipart boundary.
      return api.request<{ data: FogBugzCase; message: string }>(`/fogbugz/cases/${id}/attach`, {
        method: "POST",
        body: formData,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: fogbugzKeys.case(variables.id) });
    },
  });
}

/**
 * Response from `POST /fogbugz/cases/:id/evtx-summary` (PI-SPEC-SW-1227).
 *
 * Mirrors `CaseEvtxSummaryResult` on the API. The summary has already been
 * posted to the case as one event whose text is `summaryMarkdown`, with the
 * same content attached as `summaryFileName`.
 */
export interface EvtxSummaryResponse {
  data: {
    caseId: number;
    /** The summarized .evtx attachment's file name. */
    fileName: string;
    /** Name of the summary attachment posted to the case, e.g. `Lynx.evtx-summary.md`. */
    summaryFileName: string;
    /** True when an AI model wrote the summary; false for digest-only output. */
    aiGenerated: boolean;
    /** True when parsing stopped early at a record-count or time limit. */
    truncated: boolean;
    summaryMarkdown: string;
    digestStats: {
      recordsSeen: number;
      recordsParsed: number;
      countsByLevel: Record<string, number>;
    };
  };
  message: string;
}

/**
 * Summarize an .evtx already attached to a case (opt-in, PI-SPEC-SW-1229).
 *
 * The request downloads and parses the log server-side and may spend a model
 * call, so it can take up to ~2 minutes. No client-side timeout is set here, so
 * the API's own limits govern. On success the case is refetched so the new
 * summary event and `.evtx-summary.md` attachment appear.
 */
export function useSummarizeEvtxAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, sURL }: { id: number; sURL: string }) =>
      api.post<EvtxSummaryResponse>(`/fogbugz/cases/${String(id)}/evtx-summary`, { sURL }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: fogbugzKeys.case(variables.id) });
    },
  });
}

// ===== SpecTree Toolbox EVTX summary (epic PI-SPEC-SW-E307, ER-1663) =====

/**
 * Response from `POST /fogbugz/cases/:id/evtx-digest`: the deterministic
 * digest of one `.evtx` attachment (contract `fogbugzEvtxDigestResponseSchema`).
 * No AI model ran and nothing was written to FogBugz.
 */
export interface CaseEvtxDigestResponse {
  data: FogbugzEvtxDigestResponse;
}

/**
 * Get the deterministic digest of one `.evtx` attachment on a case, for the
 * person's own Claude to summarize in SpecTree Toolbox. Needs the `fogbugz`
 * Toolbox capability somewhere and the person's saved FogBugz key. A
 * mutation, not a query: each call downloads up to 10 MiB and is rate
 * limited, so it runs only when the person clicks Summarize. Writes nothing,
 * so nothing is invalidated.
 */
export function useCaseEvtxDigest() {
  return useMutation({
    mutationFn: ({ id, sURL }: { id: number; sURL: string }) =>
      api.post<CaseEvtxDigestResponse>(`/fogbugz/cases/${String(id)}/evtx-digest`, { sURL }),
  });
}

/**
 * Response from `POST /fogbugz/cases/:id/evtx-summary/reviewed`: the summary
 * the person reviewed, composed by the server with
 * `composeToolboxEvtxSummary` and posted to the case as one event with
 * `summaryFileName` attached. `writtenBy` is `local-claude`.
 */
export interface ReviewedEvtxSummaryResponse {
  data: EvtxSummaryResponse["data"] & { writtenBy?: "local-claude" };
  message: string;
}

/** SHA-256 of the UTF-8 encoding of `text`, as 64 lowercase hex characters (Web Crypto). */
export async function sha256Hex(text: string): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** What the page hands {@link usePostReviewedEvtxSummary}: the digest it previewed, not its hash. */
export type PostReviewedEvtxSummaryInput = Omit<FogbugzEvtxReviewedPostBody, "digestSha256"> & {
  id: number;
  /** The exact digest markdown the preview showed; its SHA-256 is sent as `digestSha256`. */
  digestMarkdown: string;
};

/**
 * Post a summary the person reviewed to the case (SpecTree Toolbox). The body
 * is the contract's `fogbugzEvtxReviewedPostBodySchema`: the attachment's
 * `sURL`, the four sections the person's own Claude wrote and the SHA-256 of
 * the digest markdown the preview showed. The server refuses with 409
 * `DIGEST_CHANGED` when its own fresh digest differs, and posts nothing. Call
 * it only from the person's explicit Post click. On success the case is
 * refetched so the new summary event and attachment appear.
 */
export function usePostReviewedEvtxSummary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, sURL, sections, digestMarkdown }: PostReviewedEvtxSummaryInput) =>
      api.post<ReviewedEvtxSummaryResponse>(`/fogbugz/cases/${String(id)}/evtx-summary/reviewed`, {
        sURL,
        sections,
        digestSha256: await sha256Hex(digestMarkdown),
      } satisfies FogbugzEvtxReviewedPostBody),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: fogbugzKeys.case(variables.id) });
    },
  });
}

/**
 * Hook to resolve a case
 */
export function useResolveCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ data: FogBugzCase; message: string }>(`/fogbugz/cases/${id}/resolve`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: fogbugzKeys.case(id) });
      queryClient.invalidateQueries({ queryKey: fogbugzKeys.cases() });
    },
  });
}

/**
 * Hook to reopen a case
 */
export function useReopenCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ data: FogBugzCase; message: string }>(`/fogbugz/cases/${id}/reopen`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: fogbugzKeys.case(id) });
      queryClient.invalidateQueries({ queryKey: fogbugzKeys.cases() });
    },
  });
}

// ===== Interview Flow Hooks (ENG-1054) =====

export interface StartInterviewResponse {
  data: {
    sessionId: string;
    firstQuestion: string;
  };
}

// ===== Pre-interview Routing (PI-SPEC-SW-978) =====

/**
 * A row from one of the FogBugz list commands.
 *
 * Mirrors `fogbugzListEntrySchema` on the server, which is a passthrough: the
 * list commands return more per row than the routing screen needs (owner,
 * primary contact, `fInactive`, …) and callers are free to read those extras.
 * Only the identifier and label are guaranteed, so extras are reached through
 * the index signature rather than declared here.
 */
export type FogbugzListEntry = Record<string, unknown>;

export interface FogbugzProjectOption extends FogbugzListEntry {
  ixProject: number;
  sProject: string;
}

export interface FogbugzAreaOption extends FogbugzListEntry {
  ixArea: number;
  sArea: string;
  ixProject: number;
}

export interface FogbugzMilestoneOption extends FogbugzListEntry {
  ixFixFor: number;
  sFixFor: string;
  /**
   * Owning project. Absent (or 0) for global milestones that apply to every
   * project — "Undecided", FogBugz's own default milestone, is one of them — so
   * callers filtering by project must treat a missing value as "global".
   */
  ixProject?: number;
}

export interface FogbugzCategoryOption extends FogbugzListEntry {
  ixCategory: number;
  sCategory: string;
}

export interface FogbugzPersonOption extends FogbugzListEntry {
  ixPerson: number;
  sPerson?: string;
  sFullName?: string;
  sEmail: string;
}

export interface FogbugzPriorityOption extends FogbugzListEntry {
  ixPriority: number;
  sPriority: string;
}

/**
 * Every FogBugz list the pre-interview routing screen chooses from.
 *
 * `areas` and `milestones` arrive whole because `cmd=listAreas` and
 * `cmd=listFixFors` take no project parameter — narrowing them to the chosen
 * project is the client's job, and the server validates only the chosen values.
 */
export interface RoutingOptions {
  projects: FogbugzProjectOption[];
  areas: FogbugzAreaOption[];
  milestones: FogbugzMilestoneOption[];
  /** Empty when `cmd=listCategories` is unavailable on this FogBugz instance. */
  categories: FogbugzCategoryOption[];
  /** Active, non-deleted people only — the server filters departed employees. */
  people: FogbugzPersonOption[];
  priorities: FogbugzPriorityOption[];
}

export interface RoutingOptionsResponse {
  data: RoutingOptions;
}

/**
 * The reporter's routing selection, sent as the body of `POST /interview/start`.
 *
 * Mirrors `routingSelectionSchema`, which is `.strict()`: only these keys are
 * accepted. Project is the only required field — there is deliberately no
 * server-side project default, so an unrouted start is rejected rather than
 * silently filed into whatever the API key points at.
 */
export interface RoutingSelection {
  ixProject: number;
  sProject: string;
  ixArea?: number;
  sArea?: string;
  ixFixFor?: number;
  sFixFor?: string;
  ixCategory?: number;
  sCategory?: string;
  ixPersonAssignedTo?: number;
  sPersonAssignedTo?: string;
  ixPriority?: number;
  /** At most 10 tags of 50 characters, matching the server schema. */
  tags?: string[];
  version?: string;
}

/**
 * Where a report will be filed, as the server resolved it from the reporter's
 * pre-interview selection.
 *
 * Mirrors `InterviewRouting` on the API. Every display name was re-derived
 * server-side from the FogBugz lists the selection was validated against, so a
 * name here can never disagree with the identifier beside it — which is what
 * makes it safe to show the reporter as the definitive statement of what
 * FogBugz will receive. Read-only by design: routing is chosen on the routing
 * screen and nowhere else.
 */
export interface InterviewRouting {
  ixProject: number;
  sProject: string;
  ixArea?: number;
  sArea?: string;
  /** Milestone (FogBugz calls it FixFor). */
  ixFixFor?: number;
  sFixFor?: string;
  ixCategory?: number;
  sCategory?: string;
  ixPersonAssignedTo?: number;
  sPersonAssignedTo?: string;
  /** Absent when the priority is left to derive from the reported severity. */
  ixPriority?: number;
  sPriority?: string;
  tags?: string[];
  version?: string;
}

/**
 * Fetch every FogBugz list the routing screen needs, in one request.
 *
 * The lists are stable for the length of an intake, so they are cached with a
 * generous `staleTime` and not refetched on focus. Failure is *not* degraded to
 * an empty payload: a reporter who cannot see the project list must be shown an
 * actionable error rather than empty selects that silently file into the API
 * key's default project (findings log items 2, 6, and 10).
 */
export function useRoutingOptions(options?: { enabled?: boolean }) {
  return useQuery<RoutingOptionsResponse>({
    queryKey: fogbugzKeys.routingOptions(),
    queryFn: () => api.get<RoutingOptionsResponse>("/fogbugz/interview/routing-options"),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export interface DedupeCase {
  ixBug: number;
  sTitle: string;
  sStatus: string;
  caseUrl: string;
  sProject?: string;
  sArea?: string;
  sPersonAssignedTo?: string;
  /** ISO 8601 UTC. */
  dtOpened?: string;
  /** ISO 8601 UTC. */
  dtLastUpdated?: string;
  /** One-line excerpt of the case's latest text, at most 240 chars. */
  sLatestTextSummary?: string;
}

export interface DedupeResponse {
  data: {
    similarCases: DedupeCase[];
    count: number;
    /**
     * True when the FogBugz search failed server-side. `count` is then 0 but
     * means "unknown", not "no duplicates"; the reporter may still submit.
     */
    unavailable?: boolean;
    /** Upstream error message when `unavailable` is true. */
    reason?: string;
  };
}

export interface AuditScore {
  /**
   * Sum of the dimensions that were actually scored. Only meaningful against
   * `maxTotal`: `0` out of `0` means no rubric ran, not a failing ticket.
   */
  overall: number;
  /** Highest reachable total. `0` means no audit rubric applies to this kind. */
  maxTotal: number;
  /** What was filed, so the display can name the kind that has no rubric. */
  kind: "bug" | "feature" | "inquiry";
  tier: "pass" | "advisory" | "critical-fail";
  issues: Array<{
    ruleId: string;
    severity: string;
    message: string;
  }>;
}

export interface SubmitInterviewResponse {
  data: {
    caseId: number;
    caseUrl: string;
    auditScore: AuditScore;
  };
  message: string;
}

export interface UploadScreenshotResponse {
  data: {
    description: string;
    filename: string;
  };
  message: string;
}

/**
 * Start a new FogBugz interview session.
 *
 * The reporter's routing selection is required: the interview is routed before
 * the first question is asked, so the case never falls through to the FogBugz
 * key's default project the way case #17054 did.
 */
export function useStartInterview() {
  return useMutation({
    mutationFn: async (routing: RoutingSelection): Promise<StartInterviewResponse> => {
      return api.post<StartInterviewResponse>("/fogbugz/interview/start", { routing });
    },
  });
}

/**
 * Upload a screenshot to an interview session
 * Returns extracted description from vision AI
 */
export function useUploadScreenshot() {
  return useMutation({
    mutationFn: async ({
      sessionId,
      file,
    }: {
      sessionId: string;
      file: File;
    }): Promise<UploadScreenshotResponse> => {
      const formData = new FormData();
      formData.append("file", file);

      // Route through ApiClient so we get auth refresh + ApiError shaping.
      // ApiClient.request() detects FormData and skips the Content-Type
      // header so the browser sets the multipart boundary.
      return api.request<UploadScreenshotResponse>(`/fogbugz/interview/${sessionId}/screenshot`, {
        method: "POST",
        body: formData,
      });
    },
  });
}

/**
 * Response from `POST /fogbugz/interview/:id/evtx` (PI-SPEC-SW-1228).
 *
 * Mirrors `EventLogUploadResult` on the API. `summaryMarkdown` and
 * `aiGenerated` are null when the reporter did not ask for a summary.
 */
export interface UploadEventLogResponse {
  data: {
    /** Zero-based position of the log in the session; addresses it for a later summary. */
    index: number;
    filename: string;
    bytes: number;
    summaryMarkdown: string | null;
    aiGenerated: boolean | null;
  };
  message: string;
}

/**
 * Upload a Windows Event Log (.evtx) to an interview session.
 *
 * `summarize` is appended *before* `file`: @fastify/multipart only exposes text
 * fields that arrive ahead of the file part, so a field sent after the file
 * would be silently ignored. The server summarizes only when the field is
 * exactly "true".
 */
export function useUploadEventLog() {
  return useMutation({
    mutationFn: async ({
      sessionId,
      file,
      summarize,
    }: {
      sessionId: string;
      file: File;
      summarize: boolean;
    }): Promise<UploadEventLogResponse> => {
      const formData = new FormData();
      formData.append("summarize", String(summarize));
      formData.append("file", file);

      // ApiClient.request() skips Content-Type for FormData so the browser
      // sets the multipart boundary.
      return api.request<UploadEventLogResponse>(`/fogbugz/interview/${sessionId}/evtx`, {
        method: "POST",
        body: formData,
      });
    },
  });
}

/**
 * Summarize an event log already attached to an interview session, for when the
 * reporter ticks "Summarize" after adding it. Returns the existing summary if
 * the log already has one.
 */
export function useSummarizeInterviewEventLog() {
  return useMutation({
    mutationFn: async ({
      sessionId,
      index,
    }: {
      sessionId: string;
      index: number;
    }): Promise<UploadEventLogResponse> =>
      api.request<UploadEventLogResponse>(
        `/fogbugz/interview/${sessionId}/evtx/${String(index)}/summary`,
        { method: "POST" }
      ),
  });
}

/**
 * Slot coverage the server computed for an interview session.
 *
 * Mirrors `IntentCoverage` in `@spectree/shared`. Slot ids are transported as
 * plain strings; resolve them to reporter-facing labels with `slotLabel` from
 * `@/lib/intent-labels`.
 */
export interface InterviewCoverage {
  /**
   * The slot set the server scored this coverage against. Every list below and
   * `totalSlots` are denominated by it, so a feature interview reports a feature-sized
   * total rather than the registry superset.
   */
  kind: IntentKind;
  filled: string[];
  unknown: string[];
  missing: string[];
  requiredRemaining: string[];
  optionalRemaining: string[];
  isComplete: boolean;
  /** Number of slots that apply to `kind`, not the size of the registry superset. */
  totalSlots: number;
}

export interface IntentDocumentResponse {
  data: {
    /** Canonical `intent.md` rendered from the session's slots. */
    intentText: string;
    coverage: InterviewCoverage;
    /**
     * The Bug bar on this intent (epic PI-SPEC-SW-E309): full bug-slot coverage
     * plus the ADO grooming bar for a Bug. Submit is refused (400
     * `BELOW_BUG_BAR`) while a bug case does not pass. Optional on the type so
     * an older server's payload still reads; the server is the gate either way.
     */
    bugBar?: FogbugzBugBarResult;
    /**
     * Where this report will be filed, chosen on the routing screen before the
     * interview began. Shown read-only at review time; there is no longer a
     * project control on this screen to disagree with it.
     */
    routing: InterviewRouting;
  };
}

export interface ReviewIntentResponse {
  data: {
    /** The document as the server re-rendered it after parsing the edit. */
    intentText: string;
    coverage: InterviewCoverage;
    /** The Bug bar re-checked on the edited intent. */
    bugBar?: FogbugzBugBarResult;
    /** Unchanged by the edit — the review step owns the document, not the routing. */
    routing: InterviewRouting;
  };
  message: string;
}

/**
 * Fetch the session's `intent.md` for the review phase.
 *
 * Pass `null` to skip the request — the wizard only asks for the document once
 * it has entered the review phase.
 */
export function useIntentDocument(sessionId: string | null) {
  return useQuery({
    queryKey: sessionId
      ? fogbugzKeys.interviewIntent(sessionId)
      : ["fogbugz", "interview-intent", "none"],
    queryFn: async (): Promise<IntentDocumentResponse> => {
      if (!sessionId) throw new Error("Session ID required");
      return api.get<IntentDocumentResponse>(`/fogbugz/interview/${sessionId}/intent`);
    },
    enabled: !!sessionId,
    // The document is assembled from session state that only this wizard
    // mutates, so refetching on window focus would just clobber nothing useful.
    refetchOnWindowFocus: false,
  });
}

/**
 * Persist the reporter's edited `intent.md`.
 *
 * The server re-parses the submitted markdown and returns recomputed coverage;
 * an unparseable document comes back as a 400 (`ApiError`) rather than a
 * silently accepted edit. Call this before advancing past the review phase so
 * the filed case reflects the corrections.
 */
export function useReviewIntent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      intentText,
    }: {
      sessionId: string;
      intentText: string;
    }): Promise<ReviewIntentResponse> => {
      // The document is the only thing this endpoint accepts. Routing was
      // chosen and validated before the interview started, so there is nothing
      // here that could re-route the case.
      return api.post<ReviewIntentResponse>(`/fogbugz/interview/${sessionId}/review`, {
        intentText,
      });
    },
    onSuccess: (_result, variables) => {
      // The cached document is now stale — the session's slots were replaced.
      void queryClient.invalidateQueries({
        queryKey: fogbugzKeys.interviewIntent(variables.sessionId),
      });
    },
  });
}

/**
 * Get dedupe candidates for an interview session
 */
export function useDedupe(sessionId: string | null) {
  return useQuery({
    queryKey: sessionId ? fogbugzKeys.interview(sessionId) : ["fogbugz", "dedupe", "none"],
    queryFn: async (): Promise<DedupeResponse> => {
      if (!sessionId) throw new Error("Session ID required");
      return api.get<DedupeResponse>(`/fogbugz/interview/${sessionId}/dedupe`);
    },
    enabled: !!sessionId,
  });
}

/**
 * Submit the interview and create a FogBugz case
 */
export function useSubmitInterview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string): Promise<SubmitInterviewResponse> => {
      return api.post<SubmitInterviewResponse>(`/fogbugz/interview/${sessionId}/submit`, {});
    },
    onSuccess: () => {
      // Invalidate case list after successful submission
      queryClient.invalidateQueries({ queryKey: fogbugzKeys.cases() });
    },
  });
}
